import { msg } from 'gt-react';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import type { ApprovalAction } from './approvalState';

/**
 * The ONE place the buyer board names approval states: a one-letter code for
 * the fixed-width cell and the full name it stands for. Trouble codes its
 * reason when there is one (C = Credit Hold); a buyer's own hold is plain T.
 * Declared with msg() so they are catalog entries (translatable,
 * extractor-visible); decode them with useMessages().
 */
export interface ApprovalStateLabel {
  code: string | null;
  name: string;
}

const APPROVED: ApprovalStateLabel = {
  code: msg('A', {
    context: 'buyer line approval state shorthand for Approved',
  }),
  name: msg('Approved', { context: 'buyer line approval state' }),
};

// Auto-approved reads A too (it is approved); colour and name tell it apart.
const AUTO_APPROVED: ApprovalStateLabel = {
  code: APPROVED.code,
  name: msg('Auto-Approved', {
    context: 'buyer line approval state, approved by the system',
  }),
};

const UNAPPROVED: ApprovalStateLabel = {
  code: msg('U', {
    context: 'buyer line approval state shorthand for Unapproved',
  }),
  name: msg('Unapproved', { context: 'buyer line approval state' }),
};

const TROUBLE: ApprovalStateLabel = {
  code: msg('T', {
    context: 'buyer line approval state shorthand for Trouble',
  }),
  name: msg('Trouble', { context: 'buyer line approval state, buyer hold' }),
};

const CREDIT_HOLD: ApprovalStateLabel = {
  code: msg('C', {
    context: 'buyer line trouble shorthand for Credit Hold (customer on hold)',
  }),
  name: msg('Credit Hold', {
    context: 'buyer line trouble state, customer on credit hold',
  }),
};

const REJECTED: ApprovalStateLabel = {
  code: msg('R', {
    context: 'buyer line approval state shorthand for Rejected',
  }),
  name: msg('Rejected', { context: 'buyer line approval state' }),
};

// Released since the last refresh (GEN-3119): already on an order.
const RELEASED: ApprovalStateLabel = {
  code: msg('✓', {
    context: 'buyer line approval state shorthand for Released (a check mark)',
  }),
  name: msg('Released', {
    context: 'buyer line approval state, already released on an order today',
  }),
};

const RESTORE: ApprovalStateLabel = {
  code: null,
  name: msg('Restore', { context: 'buyer line approval menu action' }),
};

/** How the line's current state reads in its cell and tooltip. */
export function approvalStateLabel(line: AssignmentLine): ApprovalStateLabel {
  switch (line.approvalState) {
    case 'approved':
      return APPROVED;
    case 'auto_approved':
      return AUTO_APPROVED;
    case 'trouble':
      return line.troubleReason === 'credit_hold' ? CREDIT_HOLD : TROUBLE;
    case 'rejected':
      return REJECTED;
    case 'released':
      return RELEASED;
    default:
      return UNAPPROVED;
  }
}

/** How a menu action reads: the state it moves the line to. */
export const APPROVAL_ACTION_LABELS: Record<
  ApprovalAction,
  ApprovalStateLabel
> = {
  approved: APPROVED,
  unapproved: UNAPPROVED,
  trouble: TROUBLE,
  rejected: REJECTED,
  restore: RESTORE,
};

/** "A - Approved", or just the name when there is no code. */
export function formatApprovalLabel(
  label: ApprovalStateLabel,
  m: (encoded: string) => string,
): string {
  return label.code == null
    ? m(label.name)
    : `${m(label.code)} - ${m(label.name)}`;
}
